import React from 'react';
import {
  CalendarBlank, EnvelopeSimple, File, Note, Phone, PencilSimpleLine, Receipt, Ticket as TicketIcon, Users, UsersThree, type Icon,
} from '@phosphor-icons/react';
import { AnteilRing, Chip, Widget, WidgetWert } from '../../../../components/immobilien/ui';
import { MIETER_PANEL_KOPF, type MieterWidgetDef } from '../../../../components/immobilien/mieterWidgets';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import { kautionInfo } from '../../../../lib/immobilien/einheitDetail';
import { istTicketOffen } from '../../../../lib/immobilien/tickets';
import { ZAEHLER_TYPEN } from '../../../../lib/immobilien/zaehlerFormular';
import { kontoAnzeige, mietBalken, mietdauerKurz, personenText, puenktlicheMonate } from '../../../../lib/mieter/detail';
import { PORTAL_ANZEIGE, zahlungsquote } from '../../../../lib/mieter/liste';
import type { AktivitaetTyp } from '../../../../lib/kontakte/types';
import type { MieterSeitenWert } from './MieterSeitenKontext';
import { BriefeWidgetWert } from '../../dokumente/post/BriefVerlauf';

const AKT_ICON: Record<AktivitaetTyp, Icon> = {
  email: EnvelopeSimple, ticket: TicketIcon, rechnung: Receipt, dokument: File, termin: CalendarBlank, notiz: Note,
  anruf: Phone, meeting: UsersThree, aenderung: PencilSimpleLine,
};

const zaehlerTyp = (typ: string | null): string => ZAEHLER_TYPEN.find(t => t.key === typ)?.label ?? ((typ ?? '').trim() || 'Zähler');

/** Kleine Wert-Zeile: Icon · Text · rechts (Kontakt, Aktivität). */
function Zeile({ icon: IconKomp, farbe, text, rechts }: { icon: Icon; farbe: string; text: string; rechts?: string }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
      <IconKomp size={15} color={farbe} style={{ flexShrink: 0 }} />
      <span className="flex-1 min-w-0 truncate" style={{ fontWeight: 600 }}>{text}</span>
      {rechts && <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{rechts}</span>}
    </div>
  );
}

/** Ring im Akzent-Widget (Mietkonto): Zahlungsquote. */
const Ring = ({ prozent }: { prozent: number | null }) => <AnteilRing prozent={prozent} groesse={110} farbe="var(--acc-ink)" spur="var(--acc-ink-fill)" />;

export function MieterWidget({ def, s, onOeffnen }: { def: MieterWidgetDef; s: MieterSeitenWert; onOeffnen: () => void }) {
  const kopf = MIETER_PANEL_KOPF[def.key];
  const basis = { titel: kopf.widget, icon: kopf.icon, onOeffnen };
  const { daten, miete } = s;
  const m = daten.mieter;

  switch (def.key) {
    case 'konto': {
      if (s.mietkonto !== 'ok') {
        return (
          <Widget {...basis} akzent>
            <div className="flex flex-1 items-center" style={{ gap: 16 }}>
              <Ring prozent={null} />
              <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
                <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>–</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--acc-ink2)' }}>
                  {s.mietkonto === 'laedt' ? 'Wird geladen …' : s.mietkonto === 'fehler' ? 'Mietkonto nicht geladen' : s.mietkonto === 'ohne' ? 'Keinem Objekt zugeordnet' : BUCHHALTUNG_FOLGT}
                </span>
              </div>
            </div>
          </Widget>
        );
      }
      const k = s.konto;
      const a = k ? kontoAnzeige(k.saldo, miete.gesamt, puenktlicheMonate(k.months)) : null;
      return (
        <Widget {...basis} akzent>
          <div className="flex flex-1 items-center" style={{ gap: 16 }}>
            <Ring prozent={zahlungsquote(k)} />
            <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
              <span className="immo-zahl" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1 }}>{a ? a.wert : '–'}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--acc-ink2)' }}>{a ? a.sub : 'Noch keine Buchungen'}</span>
            </div>
          </div>
        </Widget>
      );
    }
    case 'miete': {
      const balken = mietBalken(miete.kalt, miete.nk + miete.weitere, miete.hk);
      return (
        <Widget {...basis}>
          <WidgetWert wert={eur(miete.gesamt)} sub={`Kalt ${eur(miete.kalt, { nachkomma: 0 })} · NK ${eur(miete.nk + miete.weitere, { nachkomma: 0 })} · Heizung ${eur(miete.hk, { nachkomma: 0 })}`} />
          {balken && (
            <div className="flex overflow-hidden" style={{ height: 8, borderRadius: 4, gap: 2, marginTop: 6 }} aria-hidden>
              <div style={{ width: `${balken.kalt}%`, background: 'var(--acc)' }} />
              <div style={{ width: `${balken.nk}%`, background: 'var(--info)' }} />
              <div style={{ width: `${balken.hk}%`, background: 'var(--bad)' }} />
            </div>
          )}
        </Widget>
      );
    }
    case 'mv': {
      const chip = s.status === 'einzug'
        ? <Chip groesse="xs" ton="info">Einzug geplant</Chip>
        : s.ende ? <Chip groesse="xs" ton="warn">Endet {deDatum(s.ende)}</Chip> : null;
      return (
        <Widget {...basis}>
          <WidgetWert wert={s.status === 'einzug' ? 'Ab ' + deDatum(s.beginn) : mietdauerKurz(s.beginn, s.heute)} sub={s.beginn ? `seit ${deDatum(s.beginn)}` : 'Mietbeginn fehlt'} />
          {chip && <span style={{ marginTop: 6 }}>{chip}</span>}
        </Widget>
      );
    }
    case 'kaution': {
      const k = kautionInfo(m);
      const art = typeof m.kaution_art === 'string' && m.kaution_art.trim() ? m.kaution_art.trim() : 'Kautionskonto';
      const chip = k.status === 'keine' ? null
        : k.status === 'hinterlegt' ? <Chip groesse="xs" ton="ok">vollständig</Chip>
          : k.status === 'teilweise' ? <Chip groesse="xs" ton="warn">teilweise</Chip>
            : <Chip groesse="xs" ton="warn">ausstehend</Chip>;
      return (
        <Widget {...basis}>
          <WidgetWert wert={k.soll > 0 ? eur(k.soll, { nachkomma: 0 }) : '–'} sub={k.soll > 0 ? art : 'Keine Kaution hinterlegt'} />
          {chip && <span style={{ marginTop: 6 }}>{chip}</span>}
        </Widget>
      );
    }
    case 'kontakt': {
      const tel = (m.mobil ?? '').trim() || (m.telefon ?? '').trim();
      const telLabel = (m.mobil ?? '').trim() ? 'Mobil' : 'Telefon';
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {tel ? <Zeile icon={Phone} farbe="var(--ok)" text={tel} rechts={telLabel} /> : <Zeile icon={Phone} farbe="var(--ink3)" text="Keine Telefonnummer" />}
            {s.email ? <Zeile icon={EnvelopeSimple} farbe="var(--info)" text={s.email} rechts="E-Mail" /> : <Zeile icon={EnvelopeSimple} farbe="var(--ink3)" text="Keine E-Mail" />}
            <Zeile icon={Users} farbe="var(--ink3)" text={personenText(s.personen)} />
          </div>
        </Widget>
      );
    }
    case 'portal': {
      const p = PORTAL_ANZEIGE[s.portal];
      const sub = !s.email ? 'E-Mail fehlt' : s.portal === 'aktiv' ? `Login ${s.email}` : s.portal === 'gesperrt' ? 'Zugang gesperrt' : 'Zugang freigeben';
      return (
        <Widget {...basis}>
          <WidgetWert wert={<span style={{ fontSize: 24, color: p.farbe }}>{p.label}</span>} sub={sub} />
        </Widget>
      );
    }
    case 'aktivitaet': {
      const liste = daten.aktivitaeten.slice(0, 3);
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {liste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Aktivität</span>}
            {liste.map((a, i) => <Zeile key={`${a.typ}-${a.id ?? i}`} icon={AKT_ICON[a.typ]} farbe="var(--ink2)" text={a.titel} rechts={deDatum(a.datum)} />)}
          </div>
        </Widget>
      );
    }
    case 'vertraege': {
      const v = daten.vertraege;
      const erster = v[0];
      const sub = !erster ? 'Keine Verträge' : erster.unbefristet || !erster.ende ? 'unbefristet' : `bis ${deDatum(erster.ende)}`;
      return <Widget {...basis}><WidgetWert wert={v.length} sub={sub} /></Widget>;
    }
    case 'dokumente': {
      const d = daten.dokumente;
      return <Widget {...basis}><WidgetWert wert={d.length} sub={d[0] ? `zuletzt ${d[0].titel || d[0].datei_name || 'Dokument'}` : 'Keine Dokumente'} /></Widget>;
    }
    case 'briefe':
      return <Widget {...basis}><BriefeWidgetWert art="mieter" id={m.id} /></Widget>;
    case 'termine': {
      const t = daten.termine;
      const naechster = t[0];
      return (
        <Widget {...basis}>
          <WidgetWert wert={naechster ? deDatum(naechster.start_datum).slice(0, 6) : '–'} sub={naechster ? naechster.titel || 'Termin' : 'Keine anstehenden Termine'} />
          {t.length > 0 && <span style={{ marginTop: 6 }}><Chip groesse="xs">{t.length} anstehend</Chip></span>}
        </Widget>
      );
    }
    case 'tickets': {
      const offen = daten.tickets.filter(t => istTicketOffen(t.status));
      return (
        <Widget {...basis}>
          <WidgetWert wert={offen.length} farbe={offen.length ? 'var(--bad)' : 'var(--ok)'}
            sub={offen.length ? `${offen[0]?.titel || 'Ticket'}${offen.length > 1 ? ` · ${offen.length} offen` : ''}` : 'Keine offenen Tickets'} />
        </Widget>
      );
    }
    case 'zaehler': {
      const z = daten.zaehler;
      const arten = [...new Set(z.map(x => zaehlerTyp(x.typ)))];
      return <Widget {...basis}><WidgetWert wert={z.length} sub={arten.length ? arten.join(', ') : 'Keine Zähler'} /></Widget>;
    }
    case 'notizen': {
      const n = daten.notizen;
      return <Widget {...basis}><WidgetWert wert={n.length} sub={n[0] ? n[0].text : 'Keine Notizen'} /></Widget>;
    }
    case 'ki':
      return (
        <Widget {...basis}>
          <span style={{ flex: 1, marginTop: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
            {s.zusammenfassung}
          </span>
        </Widget>
      );
  }
}
